import React, { useState } from 'react';
import {
  Amount,
  Badge,
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  TextInput,
  useToast,
} from '@razorpay/blade/components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import {
  EMI_STATUS_COLOR,
  EMI_STATUS_LABEL,
  MODIFY_LOAN_DETAIL_KEY,
  MODIFY_PREVIEW_KEY,
} from './constants';
import { formatMonthYearShort, normalizeLoanDetail } from './utils';
import {
  ModifyDurationModalProps,
  LoanDetail,
  RecoveryScheduleEntry,
  RecoveryScheduleTableNode,
} from './types';
import LoanRequestSuccess from 'assets/loan/loan-request-success.png';
import { overlayLayers } from 'constants/overlayLayers.generated';

export const ModifyDurationModal = ({
  isOpen,
  onDismiss,
  loanId,
  onSuccess,
  activeDrawerQueryKey,
  recoveryPreviewQueryKey,
}: ModifyDurationModalProps) => {
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [newTenure, setNewTenure] = useState('');
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [prevTenure, setPrevTenure] = useState<number | null>(null);

  const { data: loanDetailData } = useQuery({
    queryKey: [MODIFY_LOAN_DETAIL_KEY, loanId],
    queryFn: () => api.loans.getLoanDetail(loanId!),
    enabled: isOpen && !!loanId,
  });
  const loanDetail = normalizeLoanDetail<LoanDetail>(loanDetailData);

  const {
    data: modifyPreviewData,
    isFetching: isPreviewFetching,
    isError: isPreviewError,
  } = useQuery({
    queryKey: [MODIFY_PREVIEW_KEY, loanId, newTenure],
    queryFn: () => {
      const hasTenure = newTenure && Number(newTenure) > 0;
      return api.loans.recoveryPreview(
        loanId!,
        hasTenure ? 'change-tenure' : undefined,
        undefined,
        undefined,
        hasTenure ? Number(newTenure) : undefined,
      );
    },
    enabled: isOpen && !!loanId,
  });
  const rawSchedule: RecoveryScheduleEntry[] = modifyPreviewData?.data ?? [];
  const modifySchedule: RecoveryScheduleTableNode[] = rawSchedule.map((entry, idx) => ({
    ...entry,
    id: entry.id ?? idx,
  }));

  const modifyDurationMutation = useMutation({
    mutationFn: () =>
      api.loans.editLoan({ action: 'change-tenure', tenure: Number(newTenure) }, loanId!),
    onSuccess: () => {
      queryClient.invalidateQueries(['LOANS']);
      queryClient.invalidateQueries(activeDrawerQueryKey);
      queryClient.invalidateQueries(recoveryPreviewQueryKey);
      setPrevTenure(loanDetail?.tenure ?? null);
      onDismiss();
      setIsSuccessOpen(true);
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to update tenure. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    showToast({
      content: `Loan tenure updated to ${newTenure} months.`,
      color: 'positive',
      duration: 4000,
    });
    setNewTenure('');
    onSuccess();
  };

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={onDismiss} size="full" zIndex={overlayLayers.modal}>
        <ModalHeader leading="Modify Duration" />
        <ModalBody height="100%">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
            height="100%"
            overflowY="auto"
            paddingY="spacing.7">
            <Box
              display="flex"
              flexDirection="column"
              width="600px"
              backgroundColor="surface.background.gray.intense"
              borderWidth="thin"
              borderRadius="medium"
              borderColor="surface.border.gray.subtle"
              gap="spacing.8">
              <Box display="flex" flexDirection="column" gap="spacing.8">
                <Box
                  paddingX="spacing.7"
                  paddingTop="spacing.8"
                  display="flex"
                  flexDirection="column"
                  gap="spacing.8">
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
                      Tenure
                    </Heading>
                    <Text size="small" color="surface.text.gray.subtle">
                      Increase or decrease EMI tenure
                    </Text>
                  </Box>

                  <Box display="flex" flexDirection="column" gap="spacing.5">
                    <Box display="flex" justifyContent="space-between">
                      <Text size="small" weight="medium" color="surface.text.gray.muted">
                        Current remaining EMI tenure
                      </Text>
                      <Text size="small" weight="semibold" color="surface.text.gray.normal">
                        {loanDetail
                          ? `${loanDetail.remaining_duration ?? loanDetail.tenure} of ${
                              loanDetail.tenure
                            } months`
                          : '—'}
                      </Text>
                    </Box>
                    <TextInput
                      label="New duration"
                      suffix="Months"
                      type="number"
                      value={newTenure}
                      onChange={({ value }) => {
                        const cleaned = (value ?? '').replace(/[^0-9]/g, '');
                        setNewTenure(cleaned);
                      }}
                      placeholder="Enter months"
                    />
                  </Box>
                </Box>

                {isPreviewFetching && (
                  <Box paddingX="spacing.7" display="flex" flexDirection="column" gap="spacing.4">
                    <Heading size="medium" weight="semibold">
                      {newTenure && Number(newTenure) > 0 ? 'Updated Preview' : 'Current Schedule'}
                    </Heading>
                    <Box display="flex" justifyContent="center" paddingY="spacing.4">
                      <Spinner accessibilityLabel="Loading preview" size="medium" />
                    </Box>
                  </Box>
                )}

                {!isPreviewFetching && !isPreviewError && modifySchedule.length > 0 && (
                  <Box paddingX="spacing.7" display="flex" flexDirection="column">
                    <Heading size="medium" weight="semibold" marginBottom="spacing.4">
                      {newTenure && Number(newTenure) > 0 ? 'Updated Preview' : 'Current Schedule'}
                    </Heading>
                    <Table<RecoveryScheduleTableNode>
                      data={{ nodes: modifySchedule }}
                      height="290px"
                      gridTemplateColumns="1fr 1fr 1fr 1fr">
                      {(rows) => (
                        <>
                          <TableHeader>
                            <TableHeaderRow>
                              <TableHeaderCell>Month</TableHeaderCell>
                              <TableHeaderCell textAlign="right">Old EMI</TableHeaderCell>
                              <TableHeaderCell textAlign="right">New EMI</TableHeaderCell>
                              <TableHeaderCell>Status</TableHeaderCell>
                            </TableHeaderRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map((entry, idx: number) => (
                              <TableRow key={idx} item={entry}>
                                <TableCell>
                                  <Text size="small">
                                    {formatMonthYearShort(entry.payroll_month ?? entry.month)}
                                  </Text>
                                </TableCell>
                                <TableCell textAlign="right">
                                  <Amount
                                    value={
                                      entry.status === 1
                                        ? entry.emi_amount ?? entry.total_emi ?? 0
                                        : loanDetail?.monthly_total_emi ?? 0
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell textAlign="right">
                                  <Amount
                                    value={entry.emi_amount ?? entry.total_emi ?? 0}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    color={EMI_STATUS_COLOR[entry.status] ?? 'notice'}
                                    size="small">
                                    {EMI_STATUS_LABEL[entry.status] ?? 'Pending'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </>
                      )}
                    </Table>
                  </Box>
                )}
              </Box>

              <Box
                borderTopWidth="thin"
                borderTopColor="surface.border.gray.muted"
                display="flex"
                justifyContent="space-between"
                width="100%"
                paddingX="spacing.8"
                paddingY="spacing.5">
                <Button variant="secondary" onClick={onDismiss}>
                  Close
                </Button>
                <Button
                  isDisabled={!newTenure || Number(newTenure) <= 0}
                  isLoading={modifyDurationMutation.isLoading}
                  onClick={() => modifyDurationMutation.mutate()}>
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={isSuccessOpen}
        onDismiss={handleSuccessClose}
        size="small"
        zIndex={overlayLayers.modal}>
        <ModalHeader />
        <ModalBody>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="spacing.3"
            paddingY="spacing.4"
            textAlign="center">
            <Box display="flex" flexDirection="column" gap="spacing.6">
              <img src={LoanRequestSuccess} alt="Success" width={80} height={80} />
              {loanId && (
                <Text size="small" variant="body" weight="regular" color="surface.text.gray.muted">
                  ID #{loanId}
                </Text>
              )}
            </Box>
            <Box display="flex" flexDirection="column" gap="spacing.3">
              <Heading size="small" weight="semibold" color="surface.text.gray.normal">
                Loan Tenure Updated!
              </Heading>
              <Text size="small" color="surface.text.gray.muted">
                Loan duration has been updated from {prevTenure ?? '—'} months to {newTenure}{' '}
                months.
              </Text>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button isFullWidth variant="secondary" onClick={handleSuccessClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
